import { useEffect, useCallback, useState } from 'react';
import {
  IconLoader,
  ToggleInput,
  ToggleSize,
  ButtonPrimary,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import isEqual from 'lodash.isequal';
import { useAppSelector } from '@store';
import {
  useLazyGetEmployeesTransactionsTypesQuery,
  useUpdateEmployeesTransactionsTypesMutation,
} from './store/services/transactions-types';
import {
  useLazyGetPSPDetailsQuery,
  useEditPSPDetailsMutation,
} from './store/services/details';
import {
  PSPDetailsKeys,
  TransactionType,
  TransactionTypeAllowedTransfersEnum,
} from './types';
import TransactionTypeItem from '../../components/settings/transaction-types/transaction-type';

import cn from './styles.module.css';

const TransactionsTypes = () => {
  const [cashCollection, setCashCollection] = useState(false);
  const [transactionsTypesValues, setTransacationTypesValues] = useState<
    TransactionType[]
  >([]);
  const [globalError, setGlobalError] = useState('');

  const [getPspDetails] = useLazyGetPSPDetailsQuery();

  const {
    settings: {
      transactionsTypes: { values: transactionsTypes },
      details: { pspDetails },
    },
  } = useAppSelector((state) => state);

  const [getList, { isLoading }] = useLazyGetEmployeesTransactionsTypesQuery();
  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [updateEmployeesTransactionsTypes, { isLoading: requestInProgress }] =
    useUpdateEmployeesTransactionsTypesMutation();

  const isDirty = !isEqual(
    transactionsTypes?.map((item) => ({
      id: item.id,
      active: item.active,
      allowedTransfers: item.allowedTransfers,
    })),
    transactionsTypesValues?.map((item) => ({
      id: item.id,
      active: item.active,
      allowedTransfers: item.allowedTransfers,
    })),
  );

  const onSave = async () => {
    setGlobalError('');

    const updatedTypes: TransactionType[] = [];

    transactionsTypesValues?.forEach((item) => {
      const currentType = transactionsTypes?.find(
        (type) => type.id === item.id,
      );

      if (currentType) {
        if (
          !isEqual(
            {
              id: currentType.id,
              active: currentType.active,
              allowedTransfers: currentType.allowedTransfers,
            },
            {
              id: item.id,
              active: item.active,
              allowedTransfers: item.allowedTransfers,
            },
          )
        ) {
          updatedTypes.push(item);
        }
      }
    });

    if (updatedTypes?.length) {
      await Promise.all(
        updatedTypes?.map(async (item) => {
          const response = (await updateEmployeesTransactionsTypes({
            id: item.id,
            body: {
              active: item.active,
              allowedTransfers:
                item.allowedTransfers as TransactionTypeAllowedTransfersEnum[],
            },
          })) as { error?: ResponseError };

          if (response?.error) {
            responseErrorsResolver({
              errors: response?.error,
              setError: () => {},
              setGlobalError,
            });
          }
        }),
      );
    }
  };

  const getListAction = useCallback(() => {
    getList();
  }, []);

  const onEdit = async (active: boolean) => {
    await edit({
      paymentsOrdersEnabled: active,
      key: PSPDetailsKeys.PAYMENTS_ORDERS_ENABLED,
    });
  };

  useEffect(() => {
    getListAction();
  }, []);

  useEffect(() => {
    if (transactionsTypes) {
      setTransacationTypesValues(transactionsTypes);
    }
  }, [transactionsTypes]);

  useEffect(() => {
    let title = 'Transaction Settings: Transaction types';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  useEffect(() => {
    if (!pspDetails) {
      getPspDetails();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (
      pspDetails &&
      pspDetails.find((item) => item.key === 'PAYMENTS-ORDERS-ENABLED') &&
      pspDetails.find((item) => item.key === 'PAYMENTS-ORDERS-ENABLED')?.data
        .value
    ) {
      setCashCollection(
        pspDetails.find((item) => item.key === 'PAYMENTS-ORDERS-ENABLED')?.data
          .value as boolean,
      );
    }
  }, [pspDetails]);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.tabLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.transactionsTypes}>
        {transactionsTypesValues &&
          transactionsTypesValues.map((type) => (
            <TransactionTypeItem
              key={type.id}
              transactionType={type}
              transactionTypes={transactionsTypesValues}
              setTransactionTypes={setTransacationTypesValues}
            />
          ))}
        <div className={cn.transactionTypeWrapper}>
          <div className={cn.transactionTypeName}>Cash Collection</div>
          <div className={cn.transactionTypeControlsWrapper}>
            <ToggleInput
              title="Available"
              size={ToggleSize.MEDIUM}
              checked={cashCollection}
              name="paymentsOrdersEnabled"
              onChange={(e) => {
                setCashCollection(e.target.checked);
                onEdit(e.target.checked);
              }}
              disabled={editIsLoading}
            />
          </div>
        </div>
        {globalError && (
          <div className={cn.transactionTypesError}>{globalError}</div>
        )}
        <ButtonPrimary
          text={requestInProgress ? 'Loading...' : 'Save'}
          onClick={onSave}
          disabled={requestInProgress || !isDirty}
          className={cn.saveTransactionTypesButton}
          bold
        />
      </div>
    );
  }

  return <>{content}</>;
};

export default TransactionsTypes;
